<template>
  <div class="contenedor-programacion-listado-desktop">
    <!-- Resumen estadístico -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" outlined>
          <v-card-text class="d-flex align-center">
            <v-avatar color="primary" size="48" class="mr-3">
              <v-icon dark>mdi-file-document-multiple</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption grey--text">Total Oficios</div>
              <div class="text-h5 font-weight-bold">
                {{ total }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" outlined>
          <v-card-text class="d-flex align-center">
            <v-avatar color="success" size="48" class="mr-3">
              <v-icon dark>mdi-check-circle</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption grey--text">Autorizaciones</div>
              <div class="text-h5 font-weight-bold success--text">
                {{ totalAutorizaciones }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" outlined>
          <v-card-text class="d-flex align-center">
            <v-avatar color="error" size="48" class="mr-3">
              <v-icon dark>mdi-close-circle</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption grey--text">Cancelaciones</div>
              <div class="text-h5 font-weight-bold error--text">
                {{ totalCancelaciones }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="rounded-lg" outlined>
          <v-card-text class="d-flex align-center">
            <v-avatar color="info" size="48" class="mr-3">
              <v-icon dark>mdi-currency-usd</v-icon>
            </v-avatar>
            <div>
              <div class="text-caption grey--text">Importe Total</div>
              <div class="text-h6 font-weight-bold">
                {{ importeTotal | currency }}
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tabla de oficios -->
    <v-card class="rounded-lg">
      <v-data-table
        :headers="headers"
        :items="listadoOficiosAutorizacion"
        :options.sync="opcionesTabla"
        :server-items-length="total"
        :loading="cargando"
        :footer-props="footerProps"
        item-key="id"
        :item-class="getRowClass"
        class="oficios-table"
        @click:row="verDetalle">
        <!-- Número de oficio -->
        <template v-slot:[`item.numero`]="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar
              :color="item.tipo === 'AUTORIZACIÓN' ? 'success' : 'error'"
              size="32"
              class="mr-3">
              <v-icon dark small>
                {{ item.tipo === 'AUTORIZACIÓN' ? 'mdi-check' : 'mdi-close' }}
              </v-icon>
            </v-avatar>
            <div>
              <span class="font-weight-bold text-body-1">
                {{ item.numero }}
              </span>
              <div class="text-caption grey--text">
                {{ item.detalles ? item.detalles.length : 0 }} recursos
              </div>
            </div>
          </div>
        </template>

        <!-- Tipo con chip -->
        <template v-slot:[`item.tipo`]="{ item }">
          <v-chip
            small
            :color="item.tipo === 'AUTORIZACIÓN' ? 'success' : 'error'"
            text-color="white">
            {{ item.tipo }}
          </v-chip>
        </template>

        <!-- Fecha -->
        <template v-slot:[`item.fecha`]="{ item }">
          <span class="text-body-2">
            {{ formatearFecha(item.fecha) }}
          </span>
        </template>

        <!-- Estado de vencimiento -->
        <template v-slot:[`item.vencimiento`]="{ item }">
          <v-chip
            small
            :color="getColorVencimiento(item)"
            :text-color="getColorVencimiento(item) === 'warning' ? 'black' : 'white'"
            class="font-weight-medium">
            <v-icon left x-small>{{ getIconoVencimiento(item) }}</v-icon>
            {{ getTextoVencimiento(item) }}
          </v-chip>
          <div
            v-if="item.fechaVencimiento"
            class="text-caption grey--text mt-1">
            {{ formatearFecha(item.fechaVencimiento) }}
          </div>
        </template>

        <!-- Estado de asignación -->
        <template v-slot:[`item.estado`]="{ item }">
          <div style="min-width: 120px;">
            <div class="d-flex justify-space-between mb-1">
              <span class="text-caption grey--text">Asignado</span>
              <span class="text-caption font-weight-medium">
                {{ getPorcentajeAsignacion(item) }}%
              </span>
            </div>
            <v-progress-linear
              :value="getPorcentajeAsignacion(item)"
              :color="getColorAsignacion(item)"
              height="6"
              rounded>
            </v-progress-linear>
          </div>
        </template>

        <!-- Importe -->
        <template v-slot:[`item.importe`]="{ item }">
          <div class="text-right">
            <span class="font-weight-bold text-body-1">
              {{ item.importe | currency }}
            </span>
            <div
              v-if="getImporteAsignado(item) > 0"
              class="text-caption"
              :class="getColorAsignacion(item) + '--text'">
              {{ getImporteAsignado(item) | currency }} asignado
            </div>
          </div>
        </template>

        <!-- PDF -->
        <template v-slot:[`item.anexo`]="{ item }">
          <v-tooltip v-if="item.fileData" top>
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="primary"
                v-bind="attrs"
                v-on="on"
                @click.stop="verPdf(item)">
                <v-icon small>mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
            <span>Ver PDF</span>
          </v-tooltip>
          <span v-else class="grey--text text-caption">—</span>
        </template>

        <!-- Acciones -->
        <template v-if="!soloLectura" v-slot:[`item.acciones`]="{ item }">
          <opciones :oficio="item"></opciones>
        </template>

        <!-- Empty state -->
        <template v-slot:no-data>
          <div class="text-center py-8">
            <v-icon size="64" color="grey lighten-1">
              mdi-file-document-outline
            </v-icon>
            <div class="text-h6 grey--text mt-4">
              No hay oficios registrados
            </div>
            <div class="text-body-2 grey--text">
              Los oficios de autorización aparecerán aquí
            </div>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Ventana Ver Pdf -->
    <v-dialog
      v-if="mostrarVerPdf"
      v-model="mostrarVerPdf"
      persistent
      scrollable
      width="750px">
      <v-card class="rounded-lg">
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">mdi-file-pdf-box</v-icon>
          {{ esteOficio.numero }}
          <v-spacer></v-spacer>
          <v-btn icon @click="mostrarVerPdf = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-0">
          <VerPdf
            :rutaArchivo="esteOficio.fileData"
            :nombreArchivoDescarga="nombreArchivoDescarga"/>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import eventBus from '@/helpers/eventBus';
import Opciones from '@/views/administracion/programacion/listado/Opciones.vue';
import VerPdf from '@/components/administracion/auditorias/auditoria/verPdf.vue';

export default {
  name: 'ProgramacionListadoDesktop',
  components: {
    Opciones,
    VerPdf,
  },
  props: {
    soloLectura: {
      type: Boolean,
      required: false,
      default: false,
    },
    listadoOficiosAutorizacion: {
      type: Array,
      required: true,
    },
    total: {
      type: Number,
      default: 0,
    },
    totalAutorizaciones: {
      type: Number,
      default: 0,
    },
    totalCancelaciones: {
      type: Number,
      default: 0,
    },
    importeTotal: {
      type: Number,
      default: 0,
    },
    cargando: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      opcionesTabla: {
        page: 1,
        itemsPerPage: 15,
        sortBy: ['fecha'],
        sortDesc: [true],
      },
      footerProps: {
        'items-per-page-options': [10, 15, 25, 50],
        'items-per-page-text': 'Registros por página:',
        'show-first-last-page': true,
      },
      // Mapeo de columnas a campos del backend
      mapeoOrdenamiento: {
        fecha: 'fecha',
        vencimiento: 'fechaVencimiento',
        tipo: 'tipo',
        importe: 'importe',
      },
      headers: [
        {
          text: 'Oficio',
          align: 'start',
          sortable: true,
          value: 'numero',
          width: '30%',
        },
        {
          text: 'Tipo',
          value: 'tipo',
          align: 'center',
          width: '15%',
        },
        {
          text: 'Fecha',
          value: 'fecha',
          width: '12%',
        },
        {
          text: 'Vencimiento',
          value: 'vencimiento',
          align: 'center',
          width: '12%',
        },
        {
          text: 'Asignación',
          value: 'estado',
          sortable: false,
          width: '12%',
        },
        {
          text: 'Importe',
          value: 'importe',
          align: 'end',
          width: '15%',
        },
        {
          text: 'PDF',
          value: 'anexo',
          sortable: false,
          align: 'center',
          width: '5%',
        },
        {
          text: '',
          value: 'acciones',
          sortable: false,
          align: 'end',
          width: '5%',
        },
      ],
      mostrarVerPdf: false,
      esteOficio: {},
      nombreArchivoDescarga: null,
    };
  },
  filters: {
    currency(value) {
      if (!value && value !== 0) return '$0.00';
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
      }).format(value);
    },
  },
  watch: {
    opcionesTabla: {
      handler(nuevasOpciones) {
        const sortBy = nuevasOpciones.sortBy[0] || 'fecha';
        const ordenarPor = this.mapeoOrdenamiento[sortBy] || 'fecha';
        const direccion = nuevasOpciones.sortDesc[0] ? 'DESC' : 'ASC';

        this.$emit('actualizar-opciones', {
          pagina: nuevasOpciones.page,
          limite: nuevasOpciones.itemsPerPage,
          ordenarPor,
          direccion,
        });
      },
      deep: true,
    },
  },
  methods: {
    verDetalle(oficio) {
      this.$router.push({
        name: 'oficioAutorizacion',
        params: { idOficio: oficio.id },
      });
    },
    formatearFecha(fecha) {
      if (!fecha) return '';
      return this.$moment(fecha).format('DD/MM/YYYY');
    },
    getImporteAsignado(oficio) {
      if (!oficio.detalles) return 0;
      return oficio.detalles.reduce((sum, d) => sum + (d.importe || 0), 0);
    },
    getPorcentajeAsignacion(oficio) {
      if (!oficio.importe || oficio.importe === 0) return 0;
      const asignado = this.getImporteAsignado(oficio);
      return Math.min(100, Math.round((asignado / oficio.importe) * 100));
    },
    getColorAsignacion(oficio) {
      const porcentaje = this.getPorcentajeAsignacion(oficio);
      if (porcentaje >= 100) return 'success';
      if (porcentaje >= 75) return 'info';
      if (porcentaje >= 50) return 'warning';
      return 'error';
    },
    getRowClass() {
      return 'clickable-row';
    },
    getEstadoVencimiento(oficio) {
      if (!oficio.fechaVencimiento) return 'sin-fecha';
      const hoy = this.$moment();
      const vencimiento = this.$moment(oficio.fechaVencimiento);
      const diasRestantes = vencimiento.diff(hoy, 'days');
      if (diasRestantes < 0) return 'vencido';
      if (diasRestantes <= 30) return 'por-vencer';
      return 'vigente';
    },
    getColorVencimiento(oficio) {
      const estado = this.getEstadoVencimiento(oficio);
      const colores = {
        vigente: 'success',
        'por-vencer': 'warning',
        vencido: 'error',
        'sin-fecha': 'grey',
      };
      return colores[estado] || 'grey';
    },
    getIconoVencimiento(oficio) {
      const estado = this.getEstadoVencimiento(oficio);
      const iconos = {
        vigente: 'mdi-check-circle',
        'por-vencer': 'mdi-alert-circle',
        vencido: 'mdi-close-circle',
        'sin-fecha': 'mdi-help-circle',
      };
      return iconos[estado] || 'mdi-help-circle';
    },
    getTextoVencimiento(oficio) {
      const estado = this.getEstadoVencimiento(oficio);
      if (estado === 'sin-fecha') return 'Sin fecha';
      if (estado === 'vencido') return 'Vencido';
      if (estado === 'por-vencer') {
        const dias = this.$moment(oficio.fechaVencimiento).diff(
          this.$moment(), 'days',
        );
        return dias === 0 ? 'Hoy' : `${dias} días`;
      }
      return 'Vigente';
    },
    verPdf(item) {
      this.esteOficio = item;
      this.nombreArchivoDescarga = `OficioAutorizacion-${item.numero}`;
      this.mostrarVerPdf = true;
    },
    borrarDetalle(oficioAutorizacionDetalle) {
      eventBus.$emit(
        'eliminar-oficio-autorizacion-detalle',
        oficioAutorizacionDetalle,
      );
    },
  },
};
</script>
<style lang="scss" scoped>
.oficios-table {
  ::v-deep tbody tr {
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: rgba(var(--v-primary-base), 0.04) !important;
    }
  }

  ::v-deep .clickable-row {
    cursor: pointer;
  }
}
</style>
